import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { attendanceRegistersApi, pdvApi, teachersApi, registerTemplatesApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ClipboardList,
  Plus,
  Search,
  Building,
  User,
  Printer,
  Eye,
  Calendar,
  Users,
  FileText,
  CheckCircle,
  Clock,
  Filter,
  Download,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const STATUS_BADGES = {
  draft: { label: 'Bozza', class: 'bg-gray-100 text-gray-800' },
  submitted: { label: 'Consegnato', class: 'bg-blue-100 text-blue-800' },
  printed: { label: 'Stampato', class: 'bg-emerald-100 text-emerald-800' }
};

const CreateRegisterForm = ({ pdvs, teachers, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    pdv_id: '',
    course_name: '',
    course_date: new Date().toISOString().split('T')[0],
    duration_hours: 4,
    teacher_id: '',
    course_location: '',
    notes: '',
    // v36 - campi estesi per template DOCX
    codice_progetto: '',
    tipologia_corso: '',
    ore_totali: '',
    data_inizio: '',
    data_fine: '',
    ora_inizio: '',
    ora_fine: '',
    docente_nome: '',
    codocente_nome: '',
    aula: '',
    requires_signature: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.pdv_id || !formData.course_name || !formData.course_date) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Unità Locale *</Label>
        <Select
          value={formData.pdv_id}
          onValueChange={(value) => setFormData({ ...formData, pdv_id: value })}
        >
          <SelectTrigger data-testid="register-pdv-select">
            <SelectValue placeholder="Seleziona UL" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>
                {pdv.name} - {pdv.city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Il registro includerà automaticamente tutto il personale della UL selezionata
        </p>
      </div>

      <div className="space-y-2">
        <Label>Nome Corso *</Label>
        <Input
          value={formData.course_name}
          onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
          placeholder="Es: Formazione Sicurezza Generale"
          required
          data-testid="register-course-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Data Corso *</Label>
          <Input
            type="date"
            value={formData.course_date}
            onChange={(e) => setFormData({ ...formData, course_date: e.target.value })}
            required
            data-testid="register-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Durata (ore)</Label>
          <Input
            type="number"
            min="1"
            max="40"
            value={formData.duration_hours}
            onChange={(e) => setFormData({ ...formData, duration_hours: parseInt(e.target.value) || 4 })}
            data-testid="register-duration-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Docente</Label>
        <Select
          value={formData.teacher_id}
          onValueChange={(value) => setFormData({ ...formData, teacher_id: value })}
        >
          <SelectTrigger data-testid="register-teacher-select">
            <SelectValue placeholder="Seleziona docente (opzionale)" />
          </SelectTrigger>
          <SelectContent>
            {teachers.map((teacher) => (
              <SelectItem key={teacher.id} value={teacher.id}>
                {teacher.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Luogo del Corso</Label>
        <Input
          value={formData.course_location}
          onChange={(e) => setFormData({ ...formData, course_location: e.target.value })}
          placeholder="Lascia vuoto per usare l'indirizzo della UL"
          data-testid="register-location-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Note</Label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Note aggiuntive..."
          className="w-full min-h-[60px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
          data-testid="register-notes-input"
        />
      </div>

      {/* v36 - Dati extra per template DOCX (placeholder) */}
      <details className="border rounded-md">
        <summary className="cursor-pointer px-3 py-2 bg-muted/50 text-sm font-medium">
          Dati extra per template DOCX (opzionale)
        </summary>
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Codice Progetto</Label>
              <Input
                value={formData.codice_progetto}
                onChange={(e) => setFormData({ ...formData, codice_progetto: e.target.value })}
                placeholder="Es. PRJ-2026-001"
                data-testid="register-codice-progetto-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipologia Corso</Label>
              <Input
                value={formData.tipologia_corso}
                onChange={(e) => setFormData({ ...formData, tipologia_corso: e.target.value })}
                placeholder="Es. Antincendio Livello 2 FOR"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Ore totali</Label>
              <Input
                type="number"
                min="0"
                value={formData.ore_totali}
                onChange={(e) => setFormData({ ...formData, ore_totali: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Inizio (gg/mm/aaaa)</Label>
              <Input
                value={formData.data_inizio}
                onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })}
                placeholder="15/03/2026"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Fine</Label>
              <Input
                value={formData.data_fine}
                onChange={(e) => setFormData({ ...formData, data_fine: e.target.value })}
                placeholder="15/03/2026"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Ora Inizio</Label>
              <Input
                type="time"
                value={formData.ora_inizio}
                onChange={(e) => setFormData({ ...formData, ora_inizio: e.target.value })}
                data-testid="register-ora-inizio-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ora Fine</Label>
              <Input
                type="time"
                value={formData.ora_fine}
                onChange={(e) => setFormData({ ...formData, ora_fine: e.target.value })}
                data-testid="register-ora-fine-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Docente (nome completo)</Label>
              <Input
                value={formData.docente_nome}
                onChange={(e) => setFormData({ ...formData, docente_nome: e.target.value })}
                placeholder="Es. Ing. Mario Rossi"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Codocente (opz.)</Label>
              <Input
                value={formData.codocente_nome}
                onChange={(e) => setFormData({ ...formData, codocente_nome: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Aula / Sede svolgimento</Label>
            <Input
              value={formData.aula}
              onChange={(e) => setFormData({ ...formData, aula: e.target.value })}
              placeholder="Aula A, Via..."
            />
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              id="reg-requires-signature"
              type="checkbox"
              checked={formData.requires_signature}
              onChange={(e) => setFormData({ ...formData, requires_signature: e.target.checked })}
              className="w-4 h-4"
              data-testid="register-requires-signature"
            />
            <Label htmlFor="reg-requires-signature" className="text-xs cursor-pointer">
              Richiedi firma dei discenti (abilita link pubblico di firma — disponibile da v37)
            </Label>
          </div>
        </div>
      </details>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" disabled={loading} data-testid="register-submit-btn">
          {loading ? 'Generazione...' : 'Genera Registro'}
        </Button>
      </div>
    </form>
  );
};

const RegisterDetailDialog = ({ register, onClose, onPrint, onUpdateParticipant }) => {
  const [docxTemplates, setDocxTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [generatingDocx, setGeneratingDocx] = useState(false);
  // v37 - firma digitale
  const [signingStatus, setSigningStatus] = useState(null);
  const [generatingTokens, setGeneratingTokens] = useState(false);
  const [downloadingQr, setDownloadingQr] = useState(false);

  useEffect(() => {
    registerTemplatesApi.getAll()
      .then((r) => {
        const list = (r.data || []).filter((t) => t.is_active !== false && t.file_exists !== false);
        setDocxTemplates(list);
        const def = list.find((t) => t.is_default) || list[0];
        if (def) setSelectedTemplateId(def.id);
      })
      .catch(() => {});
  }, []);

  // Load signing status if firma è richiesta
  useEffect(() => {
    if (!register?.id || !register?.requires_signature) return;
    attendanceRegistersApi.getSigningStatus(register.id)
      .then((r) => setSigningStatus(r.data))
      .catch(() => {});
  }, [register?.id, register?.requires_signature]);

  const handleGenerateTokens = async () => {
    setGeneratingTokens(true);
    try {
      const r = await attendanceRegistersApi.generateSigningTokens(register.id);
      toast.success(`Token di firma generati per ${r.data.items.length} partecipanti`);
      const st = await attendanceRegistersApi.getSigningStatus(register.id);
      setSigningStatus(st.data);
    } catch (e) {
      toast.error('Errore generazione token: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingTokens(false);
    }
  };

  const handleDownloadQr = async () => {
    setDownloadingQr(true);
    try {
      const res = await attendanceRegistersApi.getQrPdf(register.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_firme_${register.register_code}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      // After generating QR, reload signing status (tokens may have been created)
      const st = await attendanceRegistersApi.getSigningStatus(register.id);
      setSigningStatus(st.data);
    } catch (e) {
      toast.error('Errore PDF QR: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDownloadingQr(false);
    }
  };

  const copySignUrl = (url) => {
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copiato');
  };

  if (!register) return null;

  const handleSignatureToggle = async (participantId, currentSigned) => {
    try {
      await onUpdateParticipant(register.id, [{ personnel_id: participantId, signed: !currentSigned }]);
      toast.success('Presenza aggiornata');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleGenerateDocx = async () => {
    if (!selectedTemplateId) { toast.error('Seleziona un template'); return; }
    setGeneratingDocx(true);
    try {
      const res = await registerTemplatesApi.generateForRegister(selectedTemplateId, register.id);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registro_${register.register_code}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Registro DOCX generato');
    } catch (e) {
      toast.error('Errore nella generazione DOCX: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingDocx(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          Registro Presenze - {register.register_code}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6">
        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-sm">
          <div>
            <p className="text-xs text-muted-foreground">Corso</p>
            <p className="font-medium">{register.course_name}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Data</p>
            <p className="font-medium">{register.course_date}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">UL</p>
            <p className="font-medium">{register.pdv_name}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Durata</p>
            <p className="font-medium">{register.duration_hours} ore</p>
          </div>
          {(register.ora_inizio || register.ora_fine) && (
            <div>
              <p className="text-xs text-muted-foreground">Orario lezione</p>
              <p className="font-medium">
                {register.ora_inizio || '—'}{register.ora_fine ? ` - ${register.ora_fine}` : ''}
              </p>
            </div>
          )}
          {register.codice_progetto && (
            <div>
              <p className="text-xs text-muted-foreground">Codice Progetto</p>
              <p className="font-medium">{register.codice_progetto}</p>
            </div>
          )}
          {register.docente_nome && (
            <div>
              <p className="text-xs text-muted-foreground">Docente</p>
              <p className="font-medium">{register.docente_nome}</p>
            </div>
          )}
          {register.requires_signature && (
            <div className="col-span-2">
              <Badge className="bg-amber-100 text-amber-800">
                ✍ Firma dei discenti richiesta
              </Badge>
            </div>
          )}
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">Luogo</p>
            <p className="font-medium">{register.course_location || 'N/D'}</p>
          </div>
        </div>

        {/* Participants Table */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Partecipanti ({register.participants?.length || 0})
          </h3>
          <div className="border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Codice Fiscale</TableHead>
                  <TableHead>Mansione</TableHead>
                  <TableHead className="w-24 text-center">Presente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {register.participants?.map((p, idx) => (
                  <TableRow key={p.personnel_id}>
                    <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm">{p.fiscal_code || '-'}</TableCell>
                    <TableCell className="text-sm">{p.job_title || '-'}</TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => handleSignatureToggle(p.personnel_id, p.signed)}
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                          p.signed 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                        }`}
                        title={p.signed ? 'Presente' : 'Assente'}
                      >
                        {p.signed ? '✓' : ''}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-4 border-t border-border">
          {/* Generazione DOCX da template registri */}
          {docxTemplates.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-3 bg-blue-50 rounded-md">
              <FileText className="w-4 h-4 text-blue-700" />
              <span className="text-sm font-medium text-blue-900">Genera DOCX da template:</span>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger className="w-64 h-8" data-testid="register-docx-template-select">
                  <SelectValue placeholder="Seleziona template" />
                </SelectTrigger>
                <SelectContent>
                  {docxTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}{t.is_default ? ' ★' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleGenerateDocx} disabled={generatingDocx || !selectedTemplateId}
                      data-testid="register-generate-docx-btn">
                <Download className="w-4 h-4 mr-1" />
                {generatingDocx ? 'Generazione...' : 'Genera .docx'}
              </Button>
            </div>
          )}
          {/* v37 - Firma digitale tramite link pubblico */}
          {register.requires_signature && (
            <div className="p-3 bg-amber-50 rounded-md space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-amber-900">✍ Firma digitale discenti</span>
                {signingStatus && (
                  <Badge className="bg-white border border-amber-300 text-amber-900">
                    {signingStatus.signed_count} / {signingStatus.total} firmati
                  </Badge>
                )}
                {signingStatus?.completed && (
                  <Badge className="bg-emerald-100 text-emerald-800">Tutti firmati ✓</Badge>
                )}
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={handleGenerateTokens}
                          disabled={generatingTokens} data-testid="generate-signing-tokens-btn">
                    {generatingTokens ? 'Generazione...' : 'Genera link firma'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownloadQr}
                          disabled={downloadingQr} data-testid="download-qr-pdf-btn">
                    <Download className="w-4 h-4 mr-1" /> {downloadingQr ? 'PDF...' : 'PDF QR stampabile'}
                  </Button>
                </div>
              </div>
              {signingStatus && signingStatus.items?.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-100">
                      <tr>
                        <th className="text-left px-2 py-1">Discente</th>
                        <th className="text-left px-2 py-1">Stato</th>
                        <th className="text-left px-2 py-1">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signingStatus.items.map((it) => (
                        <tr key={it.personnel_id} className="border-t">
                          <td className="px-2 py-1">{it.name}</td>
                          <td className="px-2 py-1">
                            {it.signed ? (
                              <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">✓ Firmato</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700 text-[10px]">In attesa</Badge>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            {it.url ? (
                              <button
                                className="text-blue-700 hover:underline truncate max-w-[180px] inline-block"
                                onClick={() => copySignUrl(it.url)}
                                title={it.url}
                                data-testid={`copy-sign-url-${it.personnel_id}`}
                              >
                                Copia link
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-amber-800">
                Genera i link, poi inviali ai discenti (WhatsApp/SMS/email) oppure scarica il PDF QR e fallo scansionare in aula.
                Quando tutti hanno firmato il docente riceve una email automatica.
              </p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Badge className={STATUS_BADGES[register.status]?.class}>
                {STATUS_BADGES[register.status]?.label}
              </Badge>
              {register.notes && (
                <span className="text-sm text-muted-foreground">Note: {register.notes}</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Chiudi
              </Button>
              <Button onClick={() => onPrint(register.id)} data-testid="print-register-btn">
                <Printer className="w-4 h-4 mr-2" />
                Stampa PDF
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  );
};

export default function AttendanceRegistersPage() {
  const [registers, setRegisters] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [filterPdv, setFilterPdv] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [registersRes, pdvsRes, teachersRes] = await Promise.all([
        attendanceRegistersApi.getAll(),
        pdvApi.getAll(),
        teachersApi.getAll()
      ]);
      setRegisters(registersRes.data);
      setPdvs(pdvsRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateRegister = async (data) => {
    setCreating(true);
    try {
      await attendanceRegistersApi.autoGenerate({
        pdv_id: data.pdv_id,
        course_name: data.course_name,
        course_date: data.course_date,
        duration_hours: data.duration_hours,
        teacher_id: data.teacher_id || undefined,
        course_location: data.course_location || undefined,
        notes: data.notes || undefined,
        // v36 - dati extra per template DOCX
        codice_progetto: data.codice_progetto || undefined,
        tipologia_corso: data.tipologia_corso || undefined,
        ore_totali: data.ore_totali ? parseInt(data.ore_totali) : undefined,
        data_inizio: data.data_inizio || undefined,
        data_fine: data.data_fine || undefined,
        ora_inizio: data.ora_inizio || undefined,
        ora_fine: data.ora_fine || undefined,
        docente_nome: data.docente_nome || undefined,
        codocente_nome: data.codocente_nome || undefined,
        aula: data.aula || undefined,
        requires_signature: data.requires_signature || false,
      });
      toast.success('Registro presenze generato con successo!');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione del registro');
    } finally {
      setCreating(false);
    }
  };

  const handleViewRegister = async (registerId) => {
    try {
      const res = await attendanceRegistersApi.getOne(registerId);
      setSelectedRegister(res.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento del registro');
    }
  };

  const handlePrintRegister = (registerId) => {
    const printUrl = attendanceRegistersApi.getPrintUrl(registerId);
    window.open(printUrl, '_blank');
  };

  const handleDeleteRegister = async (reg) => {
    if (!window.confirm(`Eliminare il registro "${reg.course_name}" (${reg.register_code})? L'operazione è irreversibile.`)) return;
    try {
      await attendanceRegistersApi.delete(reg.id);
      toast.success('Registro eliminato');
      setRegisters((prev) => prev.filter((r) => r.id !== reg.id));
    } catch (error) {
      toast.error('Errore eliminazione: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateParticipant = async (registerId, updates) => {
    await attendanceRegistersApi.updateParticipants(registerId, updates);
    // Refresh the selected register
    const res = await attendanceRegistersApi.getOne(registerId);
    setSelectedRegister(res.data);
    fetchData();
  };

  const getPdvName = (pdvId) => {
    const pdv = pdvs.find(p => p.id === pdvId);
    return pdv ? pdv.name : 'N/D';
  };

  const filteredRegisters = registers.filter((r) => {
    const matchesSearch = 
      r.course_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.register_code?.toLowerCase().includes(search.toLowerCase()) ||
      r.pdv_name?.toLowerCase().includes(search.toLowerCase());
    const matchesPdv = filterPdv === 'all' || r.pdv_id === filterPdv;
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesPdv && matchesStatus;
  });

  const stats = {
    total: registers.length,
    draft: registers.filter(r => r.status === 'draft').length,
    submitted: registers.filter(r => r.status === 'submitted').length,
    printed: registers.filter(r => r.status === 'printed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="attendance-registers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="page-header mb-0">
          <h1 className="page-title">Registri Formazione UL</h1>
          <p className="page-subtitle">Gestione registri presenze corsi di formazione</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="new-register-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Registro
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Totale Registri</p>
              </div>
              <ClipboardList className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-600">{stats.draft}</p>
                <p className="text-xs text-muted-foreground">Bozze</p>
              </div>
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.submitted}</p>
                <p className="text-xs text-muted-foreground">Consegnati</p>
              </div>
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats.printed}</p>
                <p className="text-xs text-muted-foreground">Stampati</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per corso, codice o UL..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="register-search-input"
          />
        </div>
        <Select value={filterPdv} onValueChange={setFilterPdv}>
          <SelectTrigger data-testid="filter-register-pdv">
            <Building className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Tutte le UL" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            <SelectItem value="all">Tutte le UL</SelectItem>
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-register-status">
            <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="draft">Bozze</SelectItem>
            <SelectItem value="submitted">Consegnati</SelectItem>
            <SelectItem value="printed">Stampati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Registers Table */}
      {filteredRegisters.length > 0 ? (
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="table-header">Codice</TableHead>
                <TableHead className="table-header">Corso</TableHead>
                <TableHead className="table-header">UL</TableHead>
                <TableHead className="table-header">Data</TableHead>
                <TableHead className="table-header">Partecipanti</TableHead>
                <TableHead className="table-header">Stato</TableHead>
                <TableHead className="table-header w-[120px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegisters.map((reg) => (
                <TableRow key={reg.id} className="data-row" data-testid={`register-row-${reg.id}`}>
                  <TableCell className="font-mono text-sm">{reg.register_code}</TableCell>
                  <TableCell className="font-medium">{reg.course_name}</TableCell>
                  <TableCell>{reg.pdv_name || getPdvName(reg.pdv_id)}</TableCell>
                  <TableCell>{reg.course_date}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      <Users className="w-3 h-3 mr-1" />
                      {reg.participant_count || reg.participants?.length || 0}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_BADGES[reg.status]?.class}>
                      {STATUS_BADGES[reg.status]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleViewRegister(reg.id)}
                        title="Visualizza"
                        data-testid={`view-register-${reg.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handlePrintRegister(reg.id)}
                        title="Stampa PDF"
                        data-testid={`print-register-${reg.id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteRegister(reg)}
                        title="Elimina registro"
                        data-testid={`delete-register-${reg.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="empty-state">
          <ClipboardList className="empty-state-icon" />
          <p className="empty-state-title">
            {search || filterPdv !== 'all' || filterStatus !== 'all'
              ? 'Nessun risultato'
              : 'Nessun registro presenze'
            }
          </p>
          <p className="empty-state-description">
            {search || filterPdv !== 'all' || filterStatus !== 'all'
              ? 'Prova a modificare i filtri'
              : 'Crea un nuovo registro selezionando una UL'
            }
          </p>
          {!search && filterPdv === 'all' && filterStatus === 'all' && (
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crea Registro
            </Button>
          )}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Genera Registro Presenze
            </DialogTitle>
          </DialogHeader>
          <CreateRegisterForm
            pdvs={pdvs}
            teachers={teachers}
            onSubmit={handleCreateRegister}
            onCancel={() => setDialogOpen(false)}
            loading={creating}
          />
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <RegisterDetailDialog
          register={selectedRegister}
          onClose={() => setDetailDialogOpen(false)}
          onPrint={handlePrintRegister}
          onUpdateParticipant={handleUpdateParticipant}
        />
      </Dialog>
    </div>
  );
}
